import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { usePartnersPortalRpcClient } from "../rpc/client-context";
import { AuthLayout } from "./auth-layout";
import { useAuthToken } from "./auth-token-context";
import * as css from "./login.module.css";

const FALLBACK_ERROR = "This sign-in link is invalid or has expired.";

export const VerifyPage = memo(function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const rpcClient = usePartnersPortalRpcClient();
  const queryClient = useQueryClient();
  const { setToken } = useAuthToken();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>(
    token ? undefined : FALLBACK_ERROR,
  );

  // Links are single-use, so StrictMode's second effect run mustn't verify.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) {
      return;
    }
    startedRef.current = true;
    rpcClient.verifyMagicLink({ token }).then(
      (result) => {
        setToken(result.authToken);
        queryClient.setQueryData(["me", result.authToken], result.partnerUser);
        navigate("/stores", { replace: true });
      },
      (err: unknown) => {
        setError(clientErrorMessage(err, FALLBACK_ERROR));
      },
    );
  }, [token, rpcClient, queryClient, setToken, navigate]);

  if (error) {
    return (
      <AuthLayout subtitle={error} title="We couldn't sign you in">
        <RedoButton
          className={css.fullWidthButton}
          hierarchy="primary"
          onClick={() => navigate("/login", { replace: true })}
          size="sm"
          text="Request a new sign-in link"
        />
      </AuthLayout>
    );
  }
  return (
    <AuthLayout subtitle="Checking your sign-in link." title="Signing you in">
      <Flex align="center" justify="center" py="4xl">
        <div className={css.loadingSpinner} />
      </Flex>
    </AuthLayout>
  );
});
